import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { formatCurrency } from "../../sdk/utils/display";

// ============================================================================
// Prop Types
// ============================================================================

export interface LoadingMessageProps {
  message?: string;
}

export interface ErrorMessageProps {
  error: string;
}

export interface EmptyMessageProps {
  message: string;
}

export interface WarningMessageProps {
  message: string;
}

export interface StatusIndicatorProps {
  isConnected: boolean;
  connectedText?: string;
  disconnectedText?: string;
}

export interface LayoutHeaderProps {
  title: React.ReactNode;
  children?: React.ReactNode;
}

export interface TableHeaderCellProps<T extends string> {
  field: T;
  currentSortField: T | null;
  sortDirection: "asc" | "desc";
  onSort: (field: T) => void;
  children: React.ReactNode;
  center?: boolean;
  left?: boolean;
}

export interface TableHeaderCellStaticProps {
  children: React.ReactNode;
  center?: boolean;
  left?: boolean;
}

export interface CancelButtonProps {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
}

export interface CloseButtonProps {
  onClick: () => void;
}

export interface ActionButtonProps {
  onClick: () => void;
  variant?: "danger" | "success";
  children: React.ReactNode;
}

export interface SliderConfirmBarProps {
  onConfirm: () => void;
  label?: string;
  confirmThreshold?: number; // Percentage (0-100) to trigger confirmation, default 90
  disabled?: boolean;
  icon?: React.ReactNode; // Icon to render on the thumb button
}

export interface NumberPadProps {
  isOpen: boolean;
  value: number;
  min: number;
  max: number;
  onConfirm: (value: number) => void;
  onCancel: () => void;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Returns a sort icon based on the current sort field and direction
 * @param field - The field being checked
 * @param currentSortField - The currently active sort field (can be null)
 * @param sortDirection - The current sort direction ("asc" | "desc")
 * @returns React node with the appropriate sort icon
 */
export function getSortIcon<T extends string>(
  field: T,
  currentSortField: T | null,
  sortDirection: "asc" | "desc",
): React.ReactNode {
  if (currentSortField !== field) {
    return (
      <span style={{ fontSize: "0.7rem", opacity: 0.5, marginLeft: "0.25rem" }}>
        ▼
      </span>
    );
  }
  return (
    <span style={{ fontSize: "0.7rem", marginLeft: "0.25rem" }}>
      {sortDirection === "asc" ? "▲" : "▼"}
    </span>
  );
}

// ============================================================================
// Message Components
// ============================================================================

/**
 * Message fragment for loading state
 */
export function LoadingMessage({
  message = "Loading...",
}: LoadingMessageProps): JSX.Element {
  return <div className="message loading">{message}</div>;
}

/**
 * Message fragment for error state
 */
export function ErrorMessage({ error }: ErrorMessageProps): JSX.Element {
  return <div className="message error">Error: {error}</div>;
}

/**
 * Message fragment for empty state
 */
export function EmptyMessage({ message }: EmptyMessageProps): JSX.Element {
  return <div className="message empty">{message}</div>;
}

/**
 * Message fragment for warning state
 */
export function WarningMessage({ message }: WarningMessageProps): JSX.Element {
  return <div className="message warning">{message}</div>;
}

// ============================================================================
// Status Components
// ============================================================================

/**
 * Status indicator showing connection status
 */
export function StatusIndicator({
  isConnected,
  connectedText = "● LIVE",
  disconnectedText = "○ OFFLINE",
}: StatusIndicatorProps): JSX.Element {
  return (
    <div className="status-indicator">
      <span
        className={`status-dot ${isConnected ? "connected" : "disconnected"}`}
      >
        {isConnected ? connectedText : disconnectedText}
      </span>
    </div>
  );
}

// ============================================================================
// Layout Components
// ============================================================================

/**
 * Layout header fragment
 */
export function LayoutHeader({
  title,
  children,
}: LayoutHeaderProps): JSX.Element {
  return (
    <div className="layout-header">
      <div className="layout-header-title">{title}</div>
      {children && <div className="layout-header-actions">{children}</div>}
    </div>
  );
}

// ============================================================================
// Table Components
// ============================================================================

/**
 * Table header cell with sorting functionality
 */
export function TableHeaderCell<T extends string>({
  field,
  currentSortField,
  sortDirection,
  onSort,
  children,
  center = false,
}: TableHeaderCellProps<T>): JSX.Element {
  return (
    <div
      className={`table-header-cell ${center ? "left" : "right"}`}
      onClick={() => onSort(field)}
    >
      {children}
      {getSortIcon(field, currentSortField, sortDirection)}
    </div>
  );
}

/**
 * Table header cell without sorting (for static headers)
 */
export function TableHeaderCellStatic({
  children,
  center = false,
}: TableHeaderCellStaticProps): JSX.Element {
  return (
    <div className={`table-header-cell ${center ? "left" : "right"}`}>
      {children}
    </div>
  );
}

// ============================================================================
// Button Components
// ============================================================================

/**
 * Cancel button fragment
 */
export function CancelButton({
  onClick,
  disabled,
}: CancelButtonProps): JSX.Element {
  console.log("[CancelButton] Rendering", {
    disabled,
    hasOnClick: !!onClick,
  });

  return (
    <button
      onClick={(e) => {
        console.log("[CancelButton] Button clicked", {
          disabled,
          event: e,
        });
        if (!disabled && onClick) {
          onClick(e);
        } else {
          console.warn("[CancelButton] Click ignored - button is disabled", {
            disabled,
          });
        }
      }}
      onMouseDown={(e) => {
        console.log("[CancelButton] Mouse down", {
          disabled,
          event: e,
        });
      }}
      className="btn btn-danger"
      disabled={disabled}
      style={{
        pointerEvents: disabled ? "none" : "auto",
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      Cancel
    </button>
  );
}

/**
 * Close button fragment
 */
export function CloseButton({ onClick }: CloseButtonProps): JSX.Element {
  return (
    <button onClick={onClick} className="btn btn-success">
      Close
    </button>
  );
}

/**
 * Action button fragment with customizable variant
 */
export function ActionButton({
  onClick,
  variant = "danger",
  children,
}: ActionButtonProps): JSX.Element {
  return (
    <button onClick={onClick} className={`btn btn-${variant}`}>
      {children}
    </button>
  );
}

/**
 * Slider confirm bar - drag to the right end to confirm
 */
export function SliderConfirmBar({
  onConfirm,
  label = "Slide to confirm",
  confirmThreshold = 90,
  disabled = false,
  icon,
}: SliderConfirmBarProps): JSX.Element {
  const [dragProgress, setDragProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!sliderRef.current || disabled) return;

      const rect = sliderRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const maxX = rect.width;
      const progress = Math.max(0, Math.min(100, (x / maxX) * 100));

      setDragProgress(progress);
    };

    const handleMouseUp = () => {
      if (disabled) return;

      if (dragProgress >= confirmThreshold) {
        onConfirm();
        // Reset after a brief delay to show completion
        setTimeout(() => {
          setDragProgress(0);
        }, 200);
      } else {
        // Reset if not confirmed
        setDragProgress(0);
      }
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragProgress, confirmThreshold, onConfirm, disabled]);

  const isConfirmed = dragProgress >= confirmThreshold;

  return (
    <div
      ref={sliderRef}
      className={`slider-confirm-bar ${disabled ? "disabled" : ""} ${isDragging ? "dragging" : ""}`}
    >
      {/* Progress fill */}
      <div
        className={`slider-confirm-bar-progress ${isConfirmed ? "confirmed" : "default"} ${isDragging ? "no-transition" : ""}`}
        style={{
          width: `${dragProgress}%`,
        }}
      />
      {/* Label */}
      <div className="slider-confirm-bar-label">
        {isConfirmed ? "Release to confirm" : label}
      </div>
      {/* Draggable thumb */}
      <div
        ref={thumbRef}
        onMouseDown={handleMouseDown}
        className={`slider-confirm-bar-thumb ${disabled ? "disabled" : ""} ${isDragging ? "dragging" : ""} ${isConfirmed ? "confirmed" : ""}`}
        style={{
          left: `${dragProgress}%`,
        }}
      >
        {icon ? (
          <span className={`slider-confirm-bar-thumb-icon ${isConfirmed ? "confirmed" : ""}`}>
            {icon}
          </span>
        ) : (
          <span className={`slider-confirm-bar-thumb-arrow ${isConfirmed ? "confirmed" : ""}`}>
            →
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Number Pad Component
// ============================================================================

/**
 * Number Pad Component for entering numeric values
 */
export function NumberPad({
  isOpen,
  value,
  min,
  max,
  onConfirm,
  onCancel,
}: NumberPadProps): JSX.Element | null {
  const [displayValue, setDisplayValue] = useState<string>("");

  // Initialize display value when modal opens
  useEffect(() => {
    if (isOpen) {
      setDisplayValue(value.toString());
    }
  }, [isOpen, value]);

  if (!isOpen) return null;

  const handleNumberClick = (num: string) => {
    const newValue = displayValue === "0" ? num : displayValue + num;
    const numValue = parseFloat(newValue);

    // Validate against min/max
    if (numValue <= max) {
      setDisplayValue(newValue);
    }
  };

  const handleDecimalClick = () => {
    if (!displayValue.includes(".")) {
      setDisplayValue(displayValue + ".");
    }
  };

  const handleBackspace = () => {
    if (displayValue.length > 1) {
      setDisplayValue(displayValue.slice(0, -1));
    } else {
      setDisplayValue("0");
    }
  };

  const handleClear = () => {
    setDisplayValue("0");
  };

  const handleConfirm = () => {
    const numValue = parseFloat(displayValue) || 0;
    const clampedValue = Math.max(min, Math.min(max, numValue));
    onConfirm(clampedValue);
  };

  const currentValue = parseFloat(displayValue) || 0;
  const isValid = currentValue >= min && currentValue <= max;

  const numberPadContent = (
    <div className="modal-overlay" onClick={onCancel} style={{ zIndex: 10000 }}>
      <div
        className="modal-content number-pad-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Enter Amount</h3>
        </div>
        <div className="modal-body">
          {/* Display at top */}
          <div className="number-pad-display">
            <div className="number-pad-display-value">
              {formatCurrency(currentValue)}
            </div>
            <div className="number-pad-display-range">
              Min: {formatCurrency(min)} | Max: {formatCurrency(max)}
            </div>
            {!isValid && currentValue > 0 && (
              <div className="number-pad-display-error">
                Value must be between {formatCurrency(min)} and{" "}
                {formatCurrency(max)}
              </div>
            )}
          </div>

          {/* Number Pad Grid */}
          <div className="number-pad-grid">
            <button
              type="button"
              className="number-pad-button"
              onClick={handleNumberClick.bind(null, "1")}
            >
              1
            </button>
            <button
              type="button"
              className="number-pad-button"
              onClick={handleNumberClick.bind(null, "2")}
            >
              2
            </button>
            <button
              type="button"
              className="number-pad-button"
              onClick={handleNumberClick.bind(null, "3")}
            >
              3
            </button>
            <button
              type="button"
              className="number-pad-button"
              onClick={handleNumberClick.bind(null, "4")}
            >
              4
            </button>
            <button
              type="button"
              className="number-pad-button"
              onClick={handleNumberClick.bind(null, "5")}
            >
              5
            </button>
            <button
              type="button"
              className="number-pad-button"
              onClick={handleNumberClick.bind(null, "6")}
            >
              6
            </button>
            <button
              type="button"
              className="number-pad-button"
              onClick={handleNumberClick.bind(null, "7")}
            >
              7
            </button>
            <button
              type="button"
              className="number-pad-button"
              onClick={handleNumberClick.bind(null, "8")}
            >
              8
            </button>
            <button
              type="button"
              className="number-pad-button"
              onClick={handleNumberClick.bind(null, "9")}
            >
              9
            </button>
            <button
              type="button"
              className="number-pad-button"
              onClick={handleDecimalClick}
            >
              .
            </button>
            <button
              type="button"
              className="number-pad-button"
              onClick={handleNumberClick.bind(null, "0")}
            >
              0
            </button>
            <button
              type="button"
              className="number-pad-button number-pad-button-action"
              onClick={handleBackspace}
            >
              ⌫
            </button>
          </div>

          {/* Action Buttons */}
          <div className="number-pad-actions">
            <button
              type="button"
              className="number-pad-action-button"
              onClick={handleClear}
            >
              Clear
            </button>
            <button
              type="button"
              className="number-pad-action-button number-pad-action-button-max"
              onClick={() => {
                setDisplayValue(max.toString());
              }}
            >
              Max
            </button>
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onCancel} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="btn btn-primary"
            disabled={!isValid}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined"
    ? createPortal(numberPadContent, document.body)
    : null;
}
